import { useCallback, useEffect, useState } from 'react';
import { api, appeler, ErreurApi, type ReponseCirconscriptions } from './api';

/**
 * TERRITOIRE (§2, §4) — les circonscriptions réelles et l'adressage Mowibo.
 *
 * ⚠️ CET ÉCRAN REMPLACE UN `JSON.stringify`. La version précédente rendait la
 * réponse brute de la couverture dans un `<pre>`, en assumant : « mieux vaut la
 * donnée telle qu'elle est qu'une mise en forme qui en cacherait la moitié ».
 * C'était juste tant qu'il n'y avait rien à montrer. Il y a maintenant 386
 * circonscriptions et quatre étages d'adressage : les laisser en JSON, c'est
 * les rendre illisibles à la personne qui en a besoin.
 *
 * ⚠️ UNE CASE DE CODE POSTAL VIDE EST EXPLIQUÉE, PAS LAISSÉE VIDE. Régions et
 * préfectures n'en portent pas — c'est le découpage postal guinéen, pas une
 * donnée manquante. Sans la phrase, quelqu'un finira par en dériver un du
 * parent, c'est-à-dire par en inventer un.
 */

/**
 * ⚠️ LES TYPES ONT DÉMÉNAGÉ DANS `api.ts`, ET CE N'EST PAS UN RANGEMENT. Ce
 * fichier les redéclarait pour un endpoint que le guichet interroge désormais
 * aussi : deux formes du même contrat divergent au premier champ ajouté, et
 * c'est le motif « un nom, une chose » que ce dépôt a déjà payé quatre fois sur
 * les pièces d'identité.
 */
type Reponse = ReponseCirconscriptions;
interface NiveauAdressage {
  rang: number; nom: string; exemple: string; detenu_par: string;
  couverture: string; remarque: string;
}
interface Adressage { niveaux: NiveauAdressage[]; perimetre: string }

const ECHELONS: Array<{ v: string; l: string }> = [
  { v: 'tous', l: 'Tous les échelons' },
  { v: 'region', l: 'Régions' },
  { v: 'prefecture', l: 'Préfectures' },
  { v: 'commune', l: 'Communes' },
  { v: 'sous_prefecture', l: 'Sous-préfectures' },
];

const LIBELLE: Record<string, string> = {
  region: 'région', prefecture: 'préfecture',
  commune: 'commune', sous_prefecture: 'sous-préfecture',
};

/**
 * ⚠️ ZÉRO N'EST PAS UNE MESURE ICI, C'EST UNE ABSENCE — et les deux colonnes le
 * disaient différemment. `population` est un `bigint` : node-pg le rend en
 * CHAÎNE, et `'0'` est truthy — la table affichait donc « 0 », c'est-à-dire
 * « cette commune a zéro habitant ». `superficie_km2` est un `numeric` rendu en
 * NOMBRE, et `0` est falsy — elle affichait « — ». La même valeur, dans deux
 * colonnes voisines, lue de deux façons dont l'une est fausse.
 *
 * Mesuré le 12/09/2026 : les 386 lignes de `maps_regions` portent population=0
 * ET area_sqkm=0. La source n'a jamais été peuplée ; aucune circonscription
 * n'est à zéro habitant. On le DIT, plutôt que de laisser le tableau l'affirmer.
 */
function chiffreOuAbsence(v: number | string | null, formater: (n: number) => string) {
  const n = typeof v === 'string' ? Number(v) : v;
  if (n == null || !Number.isFinite(n) || n === 0) {
    return <span className="masque" title="La source d'adressage ne porte pas cette donnée">non renseignée</span>;
  }
  return formater(n);
}

export function Territoire() {
  const [echelon, setEchelon] = useState('tous');
  const [q, setQ] = useState('');
  const [donnees, setDonnees] = useState<Reponse | null>(null);
  const [adressage, setAdressage] = useState<Adressage | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);

  const charger = useCallback(async () => {
    setChargement(true); setErreur(null);
    try {
      const [c, a] = await Promise.all([
        api.territoire.circonscriptions({ echelon: echelon as never, q: q.trim() || undefined }),
        adressage ? Promise.resolve(adressage) : appeler<Adressage>('/territoire/adressage'),
      ]);
      setDonnees(c); setAdressage(a);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e));
    } finally { setChargement(false); }
  }, [echelon, q, adressage]);

  useEffect(() => { void charger(); }, [echelon]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="tableau-bord">
      <div className="entete-scene">
        <h2>🏛️ Territoire et adressage</h2>
        <p className="mention">
          Le découpage administratif déclaré par l'État, avec ses codes postaux, et les quatre
          étages du système d'adressage Mowibo. Le cadastre ne crée aucune adresse : il les
          emprunte à Mowibo Maps pour situer ses parcelles.
        </p>
      </div>

      {adressage && (
        <>
          <h3 className="titre-section">Les quatre étages de l'adressage</h3>
          <table className="tableau">
            <thead><tr><th>Étage</th><th>Exemple</th><th>Tenu par</th><th>Couverture</th></tr></thead>
            <tbody>
              {adressage.niveaux.map((n) => (
                <tr key={n.rang}>
                  <td><strong>{n.nom}</strong><br /><span className="mention">{n.remarque}</span></td>
                  <td><code>{n.exemple}</code></td>
                  <td>{n.detenu_par}</td>
                  <td>{n.couverture}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mention">{adressage.perimetre}</p>
        </>
      )}

      <h3 className="titre-section">Circonscriptions</h3>
      <form className="filtres" onSubmit={(e) => { e.preventDefault(); void charger(); }}>
        <label>Échelon
          <select value={echelon} onChange={(e) => setEchelon(e.target.value)}>
            {ECHELONS.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}
          </select>
        </label>
        <label>Nom, code ou code postal
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Dixin, 10017, 017…" />
        </label>
        <button className="bouton">Chercher</button>
      </form>

      {erreur && <p className="erreur">{erreur}</p>}
      {chargement && <p className="attente">Lecture du registre territorial…</p>}

      {!chargement && donnees && (
        <>
          <p className="mention">
            {donnees.effectif} circonscription(s) —{' '}
            {Object.entries(donnees.par_echelon)
              .filter(([, n]) => n > 0)
              .map(([k, n]) => `${n} ${LIBELLE[k] ?? k}${n > 1 ? 's' : ''}`)
              .join(' · ')}
            {' '}· <strong>{donnees.avec_code_postal}</strong> portent un code postal.
          </p>
          <p className="mention">{donnees.lecture_codes_postaux}</p>

          {donnees.circonscriptions.length === 0 ? (
            <p className="indetermine">Aucune circonscription ne correspond à cette recherche.</p>
          ) : (
            <table className="tableau">
              <thead>
                <tr><th>Nom</th><th>Échelon</th><th>Code</th><th>Code postal</th><th>Population</th><th>Superficie</th></tr>
              </thead>
              <tbody>
                {donnees.circonscriptions.map((c) => (
                  <tr key={c.source_id}>
                    <td>{c.nom}{c.nom_usuel && c.nom_usuel !== c.nom && <em className="mention"> · {c.nom_usuel}</em>}</td>
                    <td>{LIBELLE[c.echelon] ?? c.echelon}</td>
                    <td><code>{c.code ?? '—'}</code></td>
                    <td>
                      {c.code_postal
                        ? <code>{c.code_postal}</code>
                        : <span className="masque" title="Le découpage postal guinéen s'arrête à la commune et à la sous-préfecture">sans objet à cet échelon</span>}
                    </td>
                    <td>{chiffreOuAbsence(c.population, (n) => n.toLocaleString('fr-FR'))}</td>
                    <td>{chiffreOuAbsence(c.superficie_km2, (n) => `${Math.round(n).toLocaleString('fr-FR')} km²`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

